// ============================================
// Prompt Config Firestore Service
// CRUD for nurse prompt versions + optimizer analyses
// ============================================

import {
  collection, doc, getDoc, getDocs, setDoc,
  query, where, orderBy, limit as firestoreLimit, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { NursePromptConfig, PromptAnalysis } from '../../types/promptConfig';

import { C_PJE_PROMPT_ANALYSES, C_PJE_PROMPT_CONFIGS } from '../../constants/collections';
const promptConfigsCol = () => collection(db, C_PJE_PROMPT_CONFIGS);
const promptAnalysesCol = () => collection(db, C_PJE_PROMPT_ANALYSES);

// Strip undefined values for Firestore compatibility
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const cleaned = { ...obj } as Record<string, unknown>;
  for (const key of Object.keys(cleaned)) {
    if (cleaned[key] === undefined) delete cleaned[key];
  }
  return cleaned as T;
}

/** Get the currently active prompt config. Returns null if none exists. */
export async function getActivePromptConfig(): Promise<NursePromptConfig | null> {
  const q = query(
    promptConfigsCol(),
    where('isActive', '==', true),
    orderBy('version', 'desc'),
    firestoreLimit(1),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return snap.docs[0].data() as NursePromptConfig;
}

/** Get a specific prompt config by version ID. */
export async function getPromptConfig(versionId: string): Promise<NursePromptConfig | null> {
  const snap = await getDoc(doc(promptConfigsCol(), versionId));
  return snap.exists() ? (snap.data() as NursePromptConfig) : null;
}

/** Get all prompt configs, ordered by version descending. */
export async function getAllPromptConfigs(): Promise<NursePromptConfig[]> {
  const q = query(promptConfigsCol(), orderBy('version', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as NursePromptConfig);
}

/** Save a new prompt config version. Deactivates all other versions first. */
export async function savePromptConfig(config: NursePromptConfig): Promise<string> {
  // Deactivate all existing active configs
  const activeQ = query(promptConfigsCol(), where('isActive', '==', true));
  const activeSnap = await getDocs(activeQ);
  for (const activeDoc of activeSnap.docs) {
    await setDoc(doc(promptConfigsCol(), activeDoc.id), { isActive: false }, { merge: true });
  }

  // Save the new config as active
  await setDoc(doc(promptConfigsCol(), config.versionId), {
    ...stripUndefined(config as unknown as Record<string, unknown>),
    _createdAt: Timestamp.now(),
  });

  return config.versionId;
}

/** Update stats on an existing prompt config (after a run completes). */
export async function updatePromptStats(
  versionId: string,
  passRate: number,
): Promise<void> {
  const existing = await getPromptConfig(versionId);
  if (!existing) return;

  const stats = existing.stats;
  const newCount = stats.runsCount + 1;
  const newAvg = Math.round(((stats.avgPassRate * stats.runsCount) + passRate) / newCount);

  await setDoc(doc(promptConfigsCol(), versionId), {
    stats: {
      runsCount: newCount,
      avgPassRate: newAvg,
      bestPassRate: Math.max(stats.bestPassRate, passRate),
      worstPassRate: stats.runsCount === 0 ? passRate : Math.min(stats.worstPassRate, passRate),
    },
  }, { merge: true });
}

/** Save a prompt analysis result. */
export async function savePromptAnalysis(analysis: PromptAnalysis): Promise<string> {
  await setDoc(doc(promptAnalysesCol(), analysis.analysisId), {
    ...stripUndefined(analysis as unknown as Record<string, unknown>),
    _createdAt: Timestamp.now(),
  });
  return analysis.analysisId;
}

/** Get the most recent analysis for a given run. */
export async function getAnalysisForRun(runId: string): Promise<PromptAnalysis | null> {
  const q = query(
    promptAnalysesCol(),
    where('runId', '==', runId),
    orderBy('createdAt', 'desc'),
    firestoreLimit(1),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return snap.docs[0].data() as PromptAnalysis;
}

/** Get all analyses, most recent first. Limited to last 20. */
export async function getRecentAnalyses(): Promise<PromptAnalysis[]> {
  const q = query(
    promptAnalysesCol(),
    orderBy('createdAt', 'desc'),
    firestoreLimit(20),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as PromptAnalysis);
}
